import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, useNavigate, Link, useParams, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { api, formatMoney, formatApiError, isKycRequiredError, BACKEND_URL } from "../../lib/api";
import PriceDisplay from "../../components/PriceDisplay";
import { useCurrency } from "../../context/CurrencyContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import CarAvailability from "./CarAvailability";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Car, ListPlus, ReceiptText, Wallet, ImagePlus, Trash2, CheckCircle2, ShieldCheck, CalendarX, MapPin, Navigation, Sparkles, Banknote, HandCoins, CreditCard, Smartphone, MessagesSquare, Star, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { carImageUrl } from "../../components/cars/CarCard";
import { ContactIcons, MessageComposeDialog } from "../../components/admin/ContactPanel";
import OwnerRateCustomerDialog from "../../components/OwnerRateCustomerDialog";
import KycPage from "../customer/KycPage";
import NegotiationsPage from "../customer/NegotiationsPage";
import KycRequiredDialog from "../../components/KycRequiredDialog";
import LocationPicker from "../../components/LocationPicker";
import LiveTripMap from "../../components/LiveTripMap";

const RWANDA_DISTRICTS = [
  "Kigali","Musanze","Huye","Rubavu","Muhanga","Nyagatare","Rwamagana",
  "Rusizi","Kayonza","Kirehe","Bugesera","Gicumbi","Kamonyi","Ngoma",
  "Rulindo","Gakenke","Burera","Nyabihu","Ngororero","Rutsiro","Karongi",
  "Nyamasheke","Nyanza","Gisagara","Nyaruguru","Ruhango","Gatsibo",
];

const nav = [
  { to: "/owner", label: "Overview", icon: Wallet, testId: "nav-owner-overview", end: true },
  { to: "/owner/cars", label: "My cars", icon: Car, testId: "nav-owner-cars" },
  { to: "/owner/cars/new", label: "List new car", icon: ListPlus, testId: "nav-owner-new" },
  { to: "/owner/bookings", label: "Bookings", icon: ReceiptText, testId: "nav-owner-bookings" },
  { to: "/owner/negotiations", label: "Price offers", icon: MessagesSquare, testId: "nav-owner-negotiations" },
  { to: "/owner/payouts", label: "Payouts", icon: Banknote, testId: "nav-owner-payouts" },
  { to: "/owner/kyc", label: "Identity verification", icon: ShieldCheck, testId: "nav-owner-kyc" },
  { to: "/contact", label: "Support", icon: MessagesSquare, testId: "nav-owner-support" },
];

function Overview() {
  const { currencies } = useCurrency();
  const [data, setData] = useState({ bookings: [] });
  const [messageTarget, setMessageTarget] = useState(null);
  useEffect(() => {
    api.get("/bookings/mine", { params: { as_role: "owner" } })
      .then(({ data: bookings }) => setData({ bookings: bookings.filter((b) => b.status !== "pending_payment") }))
      .catch(() => {});
  }, []);
  return (
    <div className="space-y-6">
      <OverviewStats />
      <Card className="border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold tracking-tight">Recent bookings</h3>
          <Button asChild variant="outline" data-testid="view-all-owner-bookings"><Link to="/owner/bookings">View all</Link></Button>
        </div>
        {data.bookings.length === 0 ? <div className="text-sm text-muted-foreground">No bookings yet.</div> :
          <div className="space-y-3">
            {data.bookings.slice(0, 5).map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 border-b border-border last:border-b-0 pb-3">
                <div>
                  <div className="font-display font-medium">{b.car_title}</div>
                  <div className="text-xs text-muted-foreground">{b.customer_name} · {b.start_date}{b.start_time ? ` ${b.start_time}` : ""} → {b.end_date}{b.end_time ? ` ${b.end_time}` : ""}</div>
                  <ContactIcons email={b.customer_email} phone={b.customer_phone} userId={b.customer_id} name={b.customer_name} isGuest={b.customer_is_guest} setMessageTarget={setMessageTarget} />
                </div>
                <div className="text-right">
                  <Badge variant="outline" className="capitalize">{b.status.replace("_", " ")}</Badge>
                  <div className="text-sm mt-1"><PriceDisplay amount={b.total} listingCurrency={b.currency} /></div>
                </div>
              </div>
            ))}
          </div>
        }
      </Card>
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
    </div>
  );
}

function OverviewStats() {
  const { currencies } = useCurrency();
  const [stats, setStats] = useState({ cars: 0, active: 0, bookings: 0, earnings: 0, currency: "RWF", pendingKyc: 0, pendingReview: 0 });
  const [kycStatus, setKycStatus] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/auth/me"), api.get("/bookings/mine", { params: { as_role: "owner" } })]).then(async ([me, b]) => {
      const carsRes = await api.get("/cars", { params: { owner_id: me.data.id, status: undefined, limit: 200 } });
      const cars = carsRes.data || [];
      const bookings = b.data || [];
      const realBookings = bookings.filter((x) => x.status !== "pending_payment");
      const earnings = bookings.filter((x) => x.payment_status === "paid").reduce((sum, x) => sum + (x.owner_net || 0), 0);
      setKycStatus(me.data?.kyc_status || "pending");
      setStats({
        cars: cars.length,
        active: cars.filter((c) => c.status === "active").length,
        bookings: realBookings.length,
        earnings,
        currency: cars[0]?.currency || "RWF",
        pendingKyc: cars.filter((c) => c.status === "pending_kyc").length,
        pendingReview: cars.filter((c) => c.status === "pending").length,
      });
    }).catch(() => {});
  }, []);
  const items = [
    { l: "Listed cars", v: stats.cars, sub: `${stats.active} active`, to: "/owner/cars" },
    { l: "Total bookings", v: stats.bookings, sub: "All time", to: "/owner/bookings" },
    { l: "Net earnings", v: <PriceDisplay amount={stats.earnings} listingCurrency={stats.currency} />, sub: "After 10% fee", to: "/owner/payouts" },
    { l: "Default currency", v: stats.currency, sub: "Listing currency" },
  ];
  return (
    <div className="space-y-4">
      {stats.pendingKyc > 0 && kycStatus && kycStatus !== "verified" && (
        <Card className="border-amber-300 bg-amber-50 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="kyc-reminder-banner">
          <div className="flex items-center gap-3">
            <ShieldCheck size={18} className="text-amber-700 shrink-0" />
            <div className="text-sm text-amber-900">
              {stats.pendingKyc} {stats.pendingKyc === 1 ? "car is" : "cars are"} waiting for identity verification to go live.
            </div>
          </div>
          <Link to="/owner/kyc">
            <Button size="sm" className="bg-amber-700 hover:bg-amber-800 text-white">Verify now</Button>
          </Link>
        </Card>
      )}
      {stats.pendingReview > 0 && (
        <Card className="border-blue-200 bg-blue-50 p-4 flex items-center gap-3" data-testid="pending-review-banner">
          <ShieldCheck size={18} className="text-blue-700 shrink-0" />
          <div className="text-sm text-blue-900">
            {stats.pendingReview} {stats.pendingReview === 1 ? "car is" : "cars are"} awaiting admin review before going live.
          </div>
        </Card>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((s, i) => {
          const card = (
            <Card className={`border-border p-5 ${s.to ? "hover:border-foreground/30 hover:shadow-sm transition-shadow cursor-pointer" : ""}`}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
              <div className="font-display text-2xl font-bold tracking-tight mt-1">{s.v}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.sub}</div>
            </Card>
          );
          return s.to ? <Link key={i} to={s.to}>{card}</Link> : <div key={i}>{card}</div>;
        })}
      </div>
    </div>
  );
}

function MyCars() {
  const { currencies, settings } = useCurrency();
  const nav = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoteCar, setPromoteCar] = useState(null);
  const [paying, setPaying] = useState(false);
  const [promoteChannel, setPromoteChannel] = useState("MOMO");
  const [promotePhone, setPromotePhone] = useState("");
  const [promotePolling, setPromotePolling] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const me = await api.get("/auth/me");
      setKycStatus(me.data?.kyc_status || "pending");
      const { data } = await api.get("/cars", { params: { owner_id: me.data.id, status: undefined } });
      setCars(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // After UrubutoPay CARD redirects back, verify the transaction and flip the SPONSORED badge on
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sponsorshipId = params.get("sponsorship");
    const transactionId = params.get("transaction_id");
    if (!sponsorshipId || !transactionId) return;

    api.post(`/sponsorship/orders/${sponsorshipId}/urubutopay-confirm`, {
      transaction_id: transactionId,
    })
      .then(({ data }) => {
        if (data.ok) {
          toast.success("Promotion activated! Your SPONSORED badge is now live.");
          load();
        } else {
          toast.info(`Payment is still processing (${data.payment_status || "pending"}). Refresh in a moment.`);
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
    window.history.replaceState({}, "", "/owner/cars");
  }, []);

  // Poll for MOMO/AIRTEL_MONEY confirmation after initiating (owner approves on their phone)
  const pollSponsorship = (orderId, transactionId, attemptsLeft = 20) => {
    if (attemptsLeft <= 0) {
      setPromotePolling(false);
      toast.info("Still waiting for confirmation. Check back shortly — your promotion will activate automatically once approved.");
      return;
    }
    api.post(`/sponsorship/orders/${orderId}/urubutopay-confirm`, { transaction_id: transactionId })
      .then(({ data }) => {
        if (data.ok) {
          setPromotePolling(false);
          toast.success("Promotion activated! Your SPONSORED badge is now live.");
          setPromoteCar(null);
          load();
        } else {
          setTimeout(() => pollSponsorship(orderId, transactionId, attemptsLeft - 1), 3000);
        }
      })
      .catch(() => setTimeout(() => pollSponsorship(orderId, transactionId, attemptsLeft - 1), 3000));
  };

  const del = async (id) => {
    if (!window.confirm("Delete this car?")) return;
    try { await api.delete(`/cars/${id}`); toast.success("Car deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePause = async (c) => {
    const next = c.status === "paused" ? "active" : "paused";
    try {
      await api.patch(`/cars/${c.id}`, { status: next });
      toast.success(next === "paused" ? "Listing paused — hidden from renters" : "Listing resumed — visible to renters again");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Step 1: open modal confirming sponsorship
  const openPromote = (car) => setPromoteCar(car);

  // Step 2: pay via UrubutoPay (MOMO, AIRTEL_MONEY, or CARD)
  const pay = async () => {
    if (!promoteCar) return;
    let normalizedPhone = "";
    if (promoteChannel !== "CARD") {
      if (!promotePhone) {
        toast.error(`Please enter your ${promoteChannel === "MOMO" ? "MTN MoMo" : "Airtel Money"} phone number`);
        return;
      }
      normalizedPhone = promotePhone.trim().replace(/\s/g, "");
      if (normalizedPhone.startsWith("0")) normalizedPhone = "+250" + normalizedPhone.slice(1);
      else if (normalizedPhone.startsWith("250")) normalizedPhone = "+" + normalizedPhone;
      else if (!normalizedPhone.startsWith("+")) normalizedPhone = "+250" + normalizedPhone;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/sponsorship/request", { car_id: promoteCar.id });
      if (data.already_active) {
        toast.info("This car is already actively sponsored.");
        setPromoteCar(null);
        setPaying(false);
        return;
      }
      const orderId = data.order?.id;
      if (!orderId) {
        toast.error("Could not create sponsorship order.");
        setPaying(false);
        return;
      }
      const { data: co } = await api.post(
        `/sponsorship/orders/${orderId}/urubutopay-initiate`,
        { channel_name: promoteChannel, phone_number: normalizedPhone, origin_url: window.location.origin },
      );
      if (promoteChannel === "CARD" && co.url) {
        window.location.href = co.url;
        return;
      }
      toast.info(co.message || "Payment request sent. Please approve on your phone.");
      setPromotePolling(true);
      pollSponsorship(orderId, co.transaction_id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <>
    <Card className="border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-semibold tracking-tight">My cars</h3>
        <Button
          className="bg-primary hover:bg-primary/90"
          data-testid="add-car-btn"
          onClick={() => nav("/owner/cars/new")}
        ><ListPlus size={14} className="mr-1.5" />List new car</Button>
      </div>
      {cars.length === 0 ? <div className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center rounded-md">No cars listed yet.</div> :
        <div className="space-y-3" data-testid="owner-cars-list">
          {cars.map((c) => (
            <div key={c.id} className="flex flex-col md:flex-row md:items-center gap-3 border-b border-border last:border-b-0 pb-3" data-testid={`owner-car-${c.id}`}>
              <div className="w-full md:w-24 h-24 rounded-md overflow-hidden bg-muted shrink-0">
                <img src={carImageUrl(c.images?.[0])} alt={c.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold flex items-center gap-2">
                  {c.title}
                  {c.sponsored && (c.sponsored_paid || c.sponsored_admin_grant) && (
                    <span className="text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-0.5 rounded-full bg-[#E97A48] text-white">Sponsored</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{c.city} · {c.car_type} · <PriceDisplay amount={c.price_per_day} listingCurrency={c.currency} />/day</div>
                <Badge variant={c.status === "active" ? "default" : "outline"} className="capitalize mt-1.5">{c.status === "pending_kyc" ? "Pending verification" : c.status === "pending" ? "Pending admin review" : c.status}</Badge>
                {c.status === "rejected" && c.rejection_reason && <div className="text-[11px] text-muted-foreground mt-1">{c.rejection_reason}</div>}
              </div>
              <div className="flex flex-wrap gap-2">
                {!c.sponsored && c.status === "active" && (
                  <Button variant="outline" onClick={() => openPromote(c)} data-testid={`promote-car-${c.id}`} className="border-[#E97A48] text-[#E97A48] hover:bg-[#E97A48]/10">
                    <Sparkles size={14} className="mr-1.5" /> Promote (RWF {Math.round((settings?.sponsorship_price_rwf ?? 50000) / 1000)}k)
                  </Button>
                )}
                {["active", "paused"].includes(c.status) && (
                  <Button variant="outline" onClick={() => togglePause(c)} data-testid={`pause-car-${c.id}`}>
                    {c.status === "paused" ? <><Play size={14} className="mr-1.5" /> Resume</> : <><Pause size={14} className="mr-1.5" /> Pause</>}
                  </Button>
                )}
                <Button variant="outline" asChild><Link to={`/owner/cars/${c.id}/availability`} data-testid={`avail-car-${c.id}`}><CalendarX size={14} className="mr-1.5" /> Calendar</Link></Button>
                <Button variant="outline" asChild><Link to={`/owner/cars/${c.id}/edit`} data-testid={`edit-car-${c.id}`}>Edit</Link></Button>
                <Button variant="ghost" size="icon" onClick={() => del(c.id)} data-testid={`delete-car-${c.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      }
    </Card>

    <Dialog open={!!promoteCar} onOpenChange={(open) => { if (!open) setPromoteCar(null); }}>
      <DialogContent className="sm:max-w-md" data-testid="promote-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Promote &ldquo;{promoteCar?.title}&rdquo;</DialogTitle>
          <DialogDescription>
            Get a SPONSORED ★ badge for <strong>{settings?.sponsorship_duration_days ?? 365} days</strong>. Your car will appear at the top of the carousel and the sponsored filter.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-border bg-muted/30 p-5 mt-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-2xl font-bold tracking-tight">RWF {(settings?.sponsorship_price_rwf ?? 50000).toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-0.5">One-time · {settings?.sponsorship_duration_days ?? 365} days exposure</div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Smartphone size={16} className="text-[#E97A48]" />
              <CreditCard size={16} className="text-[#2A9D8F]" />
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Payment method</Label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: "MOMO", label: "MTN MoMo" },
              { value: "AIRTEL_MONEY", label: "Airtel Money" },
              { value: "CARD", label: "Card" },
            ].map(({ value, label }) => (
              <div key={value} onClick={() => setPromoteChannel(value)}
                className={`text-center text-xs font-medium p-2.5 rounded-md border-2 cursor-pointer transition-colors ${promoteChannel === value ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                data-testid={`promote-channel-${value}`}
              >
                {label}
              </div>
            ))}
          </div>
          {promoteChannel !== "CARD" && (
            <div className="mt-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {promoteChannel === "MOMO" ? "MTN MoMo" : "Airtel Money"} phone number
              </Label>
              <Input
                value={promotePhone}
                onChange={(e) => setPromotePhone(e.target.value)}
                placeholder="+250 78x xxx xxx"
                className="mt-1.5 h-10"
                data-testid="promote-phone-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2 mt-4">
          <Button
            type="button"
            disabled={paying || promotePolling}
            onClick={pay}
            data-testid="pay-urubutopay-btn"
            className="w-full bg-[#E97A48] hover:bg-[#D26937] text-white h-11 rounded-full font-semibold"
          >
            {promotePolling ? "Waiting for approval…" : paying ? "Processing…" : promoteChannel === "CARD" ? "Continue to card payment" : `Pay RWF ${(settings?.sponsorship_price_rwf ?? 50000).toLocaleString()} — Continue`}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center w-full">
            Paying by bank transfer? Send RWF {(settings?.sponsorship_price_rwf ?? 50000).toLocaleString()} to Equity Bank A/C 4007100592566 or MTN MoMo 0795883652, then admin will mark your order paid.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function OwnerPayouts() {
  const { currencies } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/owner/payouts/pending").then(({ data }) => setBookings(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  const pending = [];
  const paidOut = [];
  const totalPending = 0;
  const totalPaidOut = 0;
  const currency = bookings[0]?.currency || "RWF";

  return (
    <div className="space-y-6" data-testid="owner-payouts-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-border p-6 rounded-2xl">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pending payout</div>
          <div className="font-display text-3xl font-bold mt-1 text-[#E97A48]" data-testid="pending-total">
            <PriceDisplay amount={totalPending} listingCurrency={currency} />
          </div>
          <div className="text-xs text-muted-foreground mt-1">{pending.length} booking{pending.length === 1 ? "" : "s"} awaiting disbursement</div>
        </Card>
        <Card className="border-border p-6 rounded-2xl">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Already paid out</div>
          <div className="font-display text-3xl font-bold mt-1 text-[#2A9D8F]" data-testid="paidout-total">
            <PriceDisplay amount={totalPaidOut} listingCurrency={currency} />
          </div>
          <div className="text-xs text-muted-foreground mt-1">Lifetime received</div>
        </Card>
      </div>

      <Card className="border-border p-6">
        <h3 className="font-display text-xl font-semibold tracking-tight">Pending bookings</h3>
        <p className="text-xs text-muted-foreground mt-1">Once a trip is completed, admin will disburse your earnings to your registered MoMo/bank.</p>
        {pending.length === 0 ? (
          <div className="mt-4 text-sm text-muted-foreground border border-dashed border-border rounded-md p-6 text-center">
            <HandCoins className="mx-auto text-muted-foreground/60 mb-1" size={22} /> No pending payouts right now.
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {pending.map((b) => (
              <div key={b.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-border rounded-lg p-4" data-testid={`pending-payout-row-${b.id}`}>
                <div>
                  <div className="font-display font-semibold">{b.car_title}</div>
                  <div className="text-xs text-muted-foreground">{b.start_date}{b.start_time ? ` ${b.start_time}` : ""} → {b.end_date}{b.end_time ? ` ${b.end_time}` : ""} · {b.customer_name}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold"><PriceDisplay amount={b.owner_net} listingCurrency={b.currency} /></div>
                  <Badge variant="outline" className="text-[10px] mt-1"><HandCoins size={10} className="mr-1 text-[#E97A48]" /> Awaiting transfer</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {paidOut.length > 0 && (
        <Card className="border-border p-6">
          <h3 className="font-display text-xl font-semibold tracking-tight">Disbursement history</h3>
          <div className="mt-4 space-y-2">
            {paidOut.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 border-b border-border last:border-b-0 pb-2" data-testid={`paidout-row-${b.id}`}>
                <div>
                  <div className="font-medium text-sm">{b.car_title}</div>
                  <div className="text-xs text-muted-foreground">Paid on {b.paid_out_at?.slice(0, 10) || "—"}</div>
                </div>
                <div className="font-display font-bold text-sm text-[#2A9D8F]"><PriceDisplay amount={b.owner_net} listingCurrency={b.currency} /></div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function CarForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav2 = useNavigate();
  const { refresh: refreshAuth } = useAuth();
  const { currencies, countries, settings } = useCurrency();
  const [form, setForm] = useState({
    title: "", make: "", model_name: "", year: 2024, car_type: "Sedan",
    transmission: "Automatic", seats: 5, fuel: "Gasoline",
    location: "", location_lat: null, location_lng: null,
    city: "", country_code: settings?.default_country || "RW",
    price_per_day: 50000, currency: settings?.default_currency || "RWF",
    features: "", description: "", images: [],
    provides_driver: false, driver_mandatory: false, driver_fee_per_day: "", documents: [], ownership_type: "own", authorization_doc: null,
    fixed_return_time: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const dragRef = useRef(null);
  const moveImage = (from, to) => {
    if (to < 0 || to >= (form.images || []).length) return;
    const next = [...form.images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setForm({ ...form, images: next });
  };
  const [kycDialog, setKycDialog] = useState(false);
  const [kycStatusForDialog, setKycStatusForDialog] = useState("pending");
  const [kycStatus, setKycStatus] = useState(null);
  const [idDocType, setIdDocType] = useState("national_id");
  const [idFile, setIdFile] = useState(null);
  const [idUploading, setIdUploading] = useState(false);
  const identityStatus = settings?.identity_document_settings?.status || "optional";

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setKycStatus(data?.kyc_status || "pending")).catch(() => {});
  }, []);
  const uploadIdentityDoc = async () => {
    if (!idFile) { toast.error("Please select a document"); return; }
    setIdUploading(true);
    try {
      const fd = new FormData();
      fd.append("document_type", idDocType);
      fd.append("file", idFile);
      const { data } = await api.post("/kyc/verify-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setKycStatus(data.status);
      toast.success(data.status === "verified" ? "Identity verified!" : "Uploaded — verification running in the background.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIdUploading(false);
    }
  };

  useEffect(() => {
    if (editing) {
      api.get(`/cars/${id}`).then(({ data }) => {
        setForm({
          ...data,
          features: (data.features || []).join(", "),
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const autoTitle = [form.make, form.model_name].map((v) => (v || "").trim()).filter(Boolean).join(" ");
    setForm((f) => ({ ...f, title: autoTitle }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.make, form.model_name, form.year]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - (form.images || []).length;
    if (remaining <= 0) {
      toast.error("You can upload up to 10 images");
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > toUpload.length) {
      toast(`Only the first ${remaining} image(s) were uploaded (10 max)`);
    }
    setUploading(true);
    try {
      const paths = [];
      for (const f of toUpload) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...paths] }));
      toast.success(`${paths.length} image${paths.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const imgSettings = settings?.image_settings || { min_images: 4, max_images: 10 };
    const minImages = imgSettings.min_images ?? 4;
    const maxImages = imgSettings.max_images;
    const imgCount = (form.images || []).length;
    if (imgCount < minImages || (maxImages != null && imgCount > maxImages)) {
      toast.error(maxImages != null ? `Please upload between ${minImages} and ${maxImages} images` : `Please upload at least ${minImages} images`);
      return;
    }
    if (!(form.city || "").trim()) {
      toast.error("Please select a city for this car");
      return;
    }
    if (form.provides_driver && !(Number(form.driver_fee_per_day) > 0)) {
      toast.error("Please enter a driver fee greater than 0");
      return;
    }
    if (!form.ownership_type) {
      toast.error("Please declare who owns this car");
      return;
    }
    if (form.ownership_type !== "own" && (settings?.authorization_doc_required ?? true) && !form.authorization_doc) {
      toast.error("Please upload an authorization or consent letter for non-own vehicles");
      return;
    }
    if (identityStatus === "required" && (kycStatus === null || kycStatus === "pending")) {
      toast.error("Please upload an identity document below before submitting");
      return;
    }
    if ((settings?.document_verification_mode || "backend") === "frontend") {
      const docSettings = settings?.document_settings || {};
      const requiredDocs = Object.entries(docSettings).filter(([, cfg]) => cfg.status === "required").map(([type]) => type);
      const providedDocs = new Set((form.documents || []).map((d) => d.type));
      const missingDocs = requiredDocs.filter((d) => !providedDocs.has(d));
      if (missingDocs.length) {
        toast.error(`Please upload: ${missingDocs.map((d) => docSettings[d]?.label || d).join(", ")}`);
        return;
      }
      const missingExpiry = (form.documents || []).filter(
        (d) => ["insurance", "roadworthy"].includes(d.type) && !d.expiry_date
      );
      if (missingExpiry.length) {
        toast.error(`Please enter expiry date for: ${missingExpiry.map((d) => docSettings[d.type]?.label || d.type).join(", ")}`);
        return;
      }
    }
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year),
      seats: Number(form.seats),
      price_per_day: Number(form.price_per_day),
      driver_fee_per_day: form.provides_driver ? Number(form.driver_fee_per_day) || 0 : null,
      fixed_return_time: form.fixed_return_time || null,
      features: form.features ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    delete payload.owner; delete payload.id;
    try {
      let kycStatus = null;
      if (editing) {
        await api.patch(`/cars/${id}`, payload);
        toast.success("Car updated");
        nav2("/owner/cars");
      } else {
        await api.post("/cars", payload);
        toast.success("Car submitted for review");
        try {
          const { data } = await api.get("/auth/me");
          kycStatus = data?.kyc_status;
          refreshAuth();
          try { localStorage.setItem(`rx_mode_${data.id}`, "hosting"); } catch {}
        } catch (_) {}
        if (kycStatus !== "verified") {
          setKycStatusForDialog(kycStatus || "pending");
          setKycDialog(true);
        } else {
          nav2("/owner/cars");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const handleKycDialogContinue = () => {
    nav2("/owner/cars");
  };

  return (
    <Card className="border-border p-6">
      <h3 className="font-display text-xl font-semibold tracking-tight">{editing ? "Edit car" : "List a new car"}</h3>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="car-form">
        <div className="md:col-span-2 text-xs text-muted-foreground">Listing title: <span className="font-medium text-foreground" data-testid="car-title-preview">{form.title || "—"}</span></div>
        <div><Label>Brand (Make)</Label><Input className="mt-1.5" required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="e.g. Toyota" data-testid="car-brand-input" /></div>
        <div><Label>Model</Label><Input className="mt-1.5" required value={form.model_name} onChange={(e) => setForm({ ...form, model_name: e.target.value })} placeholder="e.g. RAV4" data-testid="car-model-input" /></div>
        <div><Label>Year</Label><Input className="mt-1.5" type="number" min={1990} max={2030} required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="car-year-input" /></div>
        <div>
          <Label>Type</Label>
          <Select value={form.car_type} onValueChange={(v) => setForm({ ...form, car_type: v })}>
            <SelectTrigger className="mt-1.5" data-testid="car-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["SUV", "Sedan", "Hatchback", "Van", "Pickup"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Transmission</Label>
          <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
            <SelectTrigger className="mt-1.5" data-testid="car-trans-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["Automatic", "Manual"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Fuel</Label>
          <Select value={form.fuel} onValueChange={(v) => setForm({ ...form, fuel: v })}>
            <SelectTrigger className="mt-1.5" data-testid="car-fuel-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["Gasoline", "Diesel", "Hybrid", "Electric", "Plugin-Hybrid"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Seats</Label><Input className="mt-1.5" type="number" min={1} max={100} required value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} placeholder="e.g. 5, or 30 for a minibus" data-testid="car-seats-input" /></div>
        <div>
          <Label>City <span className="text-red-500">*</span></Label>
          <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
            <SelectTrigger className="mt-1.5" data-testid="car-city-select"><SelectValue placeholder="Select a city" /></SelectTrigger>
            <SelectContent>{RWANDA_DISTRICTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Country</Label>
          <Select value={form.country_code} onValueChange={(v) => setForm({ ...form, country_code: v })}>
            <SelectTrigger className="mt-1.5" data-testid="car-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>{countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <LocationPicker
            lat={form.location_lat}
            lng={form.location_lng}
            address={form.location}
            onChange={({ lat, lng, address }) => setForm({ ...form, location_lat: lat, location_lng: lng, location: address || form.location })}
          />
        </div>
        <div><Label>Price per day</Label><Input className="mt-1.5" type="number" min={0} step="0.01" required value={form.price_per_day} onChange={(e) => setForm({ ...form, price_per_day: e.target.value })} data-testid="car-price-input" /></div>
        <div>
          <Label>Listing currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="mt-1.5" data-testid="car-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label>Features <span className="text-muted-foreground font-normal">(optional, comma-separated)</span></Label><Input className="mt-1.5" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="Bluetooth, AC, Sunroof, Backup camera" data-testid="car-features-input" /></div>
        <div className="md:col-span-2"><Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label><Textarea className="mt-1.5" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="car-description-input" /></div>

        <div className="md:col-span-2 border border-border rounded-lg p-4 bg-card" data-testid="driver-options-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.provides_driver}
              onChange={(e) => setForm({ ...form, provides_driver: e.target.checked, driver_mandatory: false })}
              className="mt-1 h-4 w-4 rounded border-border accent-primary"
              data-testid="provides-driver-toggle"
            />
            <div className="flex-1">
              <div className="font-display font-semibold text-sm">I provide a driver with this car</div>
              <div className="text-xs text-muted-foreground mt-0.5">Enable if this car comes with a driver option. You can make it optional or mandatory below.</div>
            </div>
          </label>
          {form.provides_driver && (
            <div className="mt-3 pl-7 space-y-3">
              <div>
                <div className="text-xs font-medium mb-2">Driver availability</div>
                <div className="flex flex-col gap-2">
                  <label className={`flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${!form.driver_mandatory ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input
                      type="radio"
                      name="driver_mode"
                      checked={!form.driver_mandatory}
                      onChange={() => setForm({ ...form, driver_mandatory: false })}
                      className="accent-primary"
                      data-testid="driver-optional-radio"
                    />
                    <div>
                      <div className="text-sm font-medium">Optional</div>
                      <div className="text-xs text-muted-foreground">Renter can choose to book with or without a driver</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${form.driver_mandatory ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input
                      type="radio"
                      name="driver_mode"
                      checked={form.driver_mandatory}
                      onChange={() => setForm({ ...form, driver_mandatory: true })}
                      className="accent-primary"
                      data-testid="driver-mandatory-radio"
                    />
                    <div>
                      <div className="text-sm font-medium">Mandatory</div>
                      <div className="text-xs text-muted-foreground">Driver is always included, no self-drive option — the fee below is added to every booking</div>
                    </div>
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs">Driver fee per day (in {form.currency}) <span className="text-muted-foreground font-normal">(required)</span></Label>
                <Input
                  className={`mt-1.5 ${!(Number(form.driver_fee_per_day) > 0) ? "border-destructive focus-visible:ring-destructive" : ""}`}
                  type="number"
                  min={0.01}
                  step="0.01"
                  required
                  value={form.driver_fee_per_day}
                  onChange={(e) => setForm({ ...form, driver_fee_per_day: e.target.value })}
                  placeholder="e.g. 20000"
                  data-testid="driver-fee-input"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  {form.driver_mandatory
                    ? "Required — this fee is added automatically to every booking since the driver is mandatory."
                    : "Required — only added to the total when a renter chooses to book with a driver."}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <Label>Fixed return time <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <div className="flex items-center gap-3 mt-1.5">
            <Input
              className="w-40"
              type="time"
              value={form.fixed_return_time || ""}
              onChange={(e) => setForm({ ...form, fixed_return_time: e.target.value })}
              data-testid="car-fixed-return-time-input"
            />
            {form.fixed_return_time && (
              <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, fixed_return_time: "" })}>Clear</Button>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            If set, every renter must return this car by this time on the return date, regardless of what pickup/return times they select — e.g. set to 08:00 to require morning returns only.
          </div>
        </div>

        <div className="md:col-span-2">
          <Label>Vehicle ownership</Label>
          <div className="text-xs text-muted-foreground mt-1 mb-3">Declare who owns this vehicle. This will be verified against uploaded documents.</div>
          <div className="grid grid-cols-1 gap-2">
            {[
              { value: "own", label: "This car is registered in my name" },
              { value: "company", label: "Company car — I am authorized to list it" },
              { value: "third_party", label: "Third-party car — I have the owner's consent" },
            ].map(({ value, label }) => (
              <label key={value} className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${form.ownership_type === value ? "border-primary bg-primary/5" : "border-border"}`}>
                <input
                  type="radio"
                  name="ownership_type"
                  value={value}
                  checked={form.ownership_type === value}
                  onChange={() => setForm({ ...form, ownership_type: value, authorization_doc: null })}
                  className="accent-primary"
                  data-testid={`ownership-${value}`}
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
          {form.ownership_type !== "own" && (
            <div className="mt-3 border border-border rounded-md p-3" data-testid="auth-doc-upload">
              <div className="font-display font-semibold text-sm mb-1">
                {form.ownership_type === "company" ? "Company authorization letter" : "Owner consent letter"}
                {!(settings?.authorization_doc_required ?? true) && (
                  <span className="text-muted-foreground font-normal"> (optional)</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground mb-2">
                {form.ownership_type === "company"
                  ? "Upload a letter on company letterhead authorizing you to list this vehicle."
                  : "Upload a signed consent letter from the registered owner authorizing you to list this vehicle."}
              </div>
              {form.authorization_doc ? (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground truncate">{form.authorization_doc.split("/").pop()}</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, authorization_doc: null })}>Remove</Button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-border text-xs text-muted-foreground cursor-pointer hover:bg-muted">
                  <ImagePlus size={12} /> Upload letter
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const fd = new FormData();
                    fd.append("file", f);
                    try {
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setForm({ ...form, authorization_doc: data.path });
                      toast.success("Authorization letter uploaded");
                    } catch (err) { toast.error(formatApiError(err)); }
                  }} />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <Label>Vehicle documents</Label>
          <div className="text-xs text-muted-foreground mt-1 mb-2">Upload what you have available. Documents are verified automatically — required ones are marked below, everything else is optional and can be added later.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(settings?.document_settings || {
              logbook: { label: "Carte Jaune", status: "required", domain: "vehicle" },
              insurance: { label: "Assurance", status: "required", domain: "vehicle" },
              roadworthy: { label: "Contrôle Technique", status: "required", domain: "vehicle" },
            }).filter(([, cfg]) => cfg.status !== "not_necessary" && (cfg.domain || "vehicle") === "vehicle").map(([kind]) => kind).map((kind) => {
              const doc = (form.documents || []).find((d) => d.type === kind);
              const needsExpiry = ["insurance", "roadworthy"].includes(kind);
              return (
                <div key={kind} className="border border-border rounded-md p-3" data-testid={`doc-row-${kind}`}>
                  <div className={needsExpiry ? "flex items-center justify-between gap-2" : ""}>
                    <div className="font-display font-semibold text-sm">
                      {(settings?.document_settings || {})[kind]?.label || (kind === "logbook" ? "Carte Jaune" : kind === "insurance" ? "Assurance" : "Contrôle Technique")}
                      {(settings?.document_settings || {})[kind]?.status === "optional" && <span className="ml-1 text-[10px] font-normal text-muted-foreground">(optional)</span>}
                    </div>
                    {needsExpiry && (
                      <div className="flex flex-col items-end">
                        <div className="relative">
                          <input
                            type="date"
                            className="text-[11px] border border-border rounded-md pl-2 pr-1 py-1 bg-background w-32 text-muted-foreground"
                            value={(form.documents || []).find((d) => d.type === kind)?.expiry_date || ""}
                            min={new Date().toISOString().split("T")[0]}
                            onChange={(e) => {
                              const existing = (form.documents || []).find((d) => d.type === kind);
                              const docs = existing
                                ? (form.documents || []).map((d) => d.type === kind ? { ...d, expiry_date: e.target.value } : d)
                                : [...(form.documents || []), { type: kind, expiry_date: e.target.value }];
                              setForm({ ...form, documents: docs });
                            }}
                            data-testid={`doc-expiry-${kind}`}
                          />
                          {!(form.documents || []).find((d) => d.type === kind)?.expiry_date && (
                            <span className="pointer-events-none absolute inset-0 flex items-center pl-2 text-[11px] text-muted-foreground">Expiry date</span>
                          )}
                        </div>
                        {(form.documents || []).find((d) => d.type === kind)?.expiry_date &&
                          new Date((form.documents || []).find((d) => d.type === kind)?.expiry_date) < new Date() && (
                          <div className="text-[10px] text-destructive mt-0.5">Expired — cannot list</div>
                        )}
                      </div>
                    )}
                  </div>
                  {doc ? (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground truncate">{doc.file_path?.split("/").pop()}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setForm({ ...form, documents: form.documents.filter((d) => d.type !== kind) })}
                          data-testid={`doc-remove-${kind}`}
                        >
                          Remove
                        </Button>
                      </div>

                    </div>
                  ) : (
                    <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-border text-xs text-muted-foreground cursor-pointer hover:bg-muted" data-testid={`doc-upload-${kind}`}>
                      <ImagePlus size={12} /> Upload
                      <input
                        type="file"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const fd = new FormData();
                          fd.append("file", f);
                          fd.append("doc_type", kind);
                          try {
                            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                            const docs = [...(form.documents || []).filter((d) => d.type !== kind), {
                              type: kind, file_path: data.path, uploaded_at: new Date().toISOString(),
                            }];
                            setForm({ ...form, documents: docs });
                            toast.success(`${kind} uploaded`);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                      />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {identityStatus !== "not_necessary" && (
          <div className="md:col-span-2">
            <Label>
              Identity document
              {identityStatus === "optional" && <span className="ml-1 text-[10px] font-normal text-muted-foreground">(optional)</span>}
            </Label>
            {kycStatus === "verified" ? (
              <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 inline-flex items-center gap-1.5">
                <ShieldCheck size={14} /> Identity verified
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="text-xs text-muted-foreground">
                  {kycStatus === "submitted" ? "Uploaded — verification in progress. You can still submit this listing." : "Upload one: National ID, Passport, or Driving Licence."}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[["national_id","National ID"],["passport","Passport"],["driving_licence","Driving Licence"]].map(([val,label]) => (
                    <button key={val} type="button" onClick={() => setIdDocType(val)}
                      className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + (idDocType === val ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-border text-xs text-muted-foreground cursor-pointer hover:bg-muted">
                    <ImagePlus size={12} /> {idFile ? idFile.name : "Choose file"}
                    <input type="file" className="hidden" onChange={(e) => setIdFile(e.target.files?.[0] || null)} />
                  </label>
                  <Button type="button" size="sm" onClick={uploadIdentityDoc} disabled={idUploading || !idFile} className="bg-primary hover:bg-primary/90">
                    {idUploading ? "Uploading…" : "Upload"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="md:col-span-2">
          <Label>Images of Your Car <span className="text-muted-foreground font-normal">(required, {settings?.image_settings?.min_images ?? 4}-{settings?.image_settings?.max_images ?? 10} images, {(form.images || []).length}/{settings?.image_settings?.max_images ?? 10} — drag to reorder, first photo is the cover)</span></Label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {form.images?.map((p, i) => (
              <div
                key={i}
                data-img-index={i}
                className={`aspect-square rounded-md overflow-hidden border relative group cursor-grab active:cursor-grabbing touch-none select-none ${i === 0 ? "border-primary ring-2 ring-primary/40" : "border-border"} ${dragIndex === i ? "opacity-40" : ""}`}
                data-testid={`car-image-${i}`}
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  dragRef.current = i;
                  setDragIndex(i);
                }}
                onPointerMove={(e) => {
                  if (dragRef.current === null) return;
                  const el = document.elementFromPoint(e.clientX, e.clientY);
                  const target = el?.closest("[data-img-index]");
                  if (target) {
                    const targetIndex = parseInt(target.getAttribute("data-img-index"), 10);
                    if (!isNaN(targetIndex) && targetIndex !== dragRef.current) {
                      moveImage(dragRef.current, targetIndex);
                      dragRef.current = targetIndex;
                      setDragIndex(targetIndex);
                    }
                  }
                }}
                onPointerUp={() => { dragRef.current = null; setDragIndex(null); }}
                onPointerCancel={() => { dragRef.current = null; setDragIndex(null); }}
              >
                <img src={carImageUrl(p)} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                {i === 0 && (
                  <span className="absolute bottom-1 left-1 text-[10px] font-semibold uppercase tracking-wide bg-primary text-primary-foreground px-1.5 py-0.5 rounded">Cover</span>
                )}
                <div className="absolute top-1 left-1 flex gap-0.5">
                  {i > 0 && (
                    <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => moveImage(i, i - 1)} className="bg-background/90 border border-border rounded-full p-1" data-testid={`move-left-${i}`}>
                      <ChevronLeft size={12} />
                    </button>
                  )}
                  {i < form.images.length - 1 && (
                    <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => moveImage(i, i + 1)} className="bg-background/90 border border-border rounded-full p-1" data-testid={`move-right-${i}`}>
                      <ChevronRight size={12} />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                  className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-${i}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            {(form.images || []).length < 10 && (
              <label className="aspect-square rounded-md border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground cursor-pointer hover:bg-muted transition-colors" data-testid="upload-images-btn">
                <ImagePlus size={20} />
                <span className="text-xs mt-1">{uploading ? "Uploading…" : "Add images"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        <div className="md:col-span-2 flex gap-3 mt-2">
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="car-save-btn">{saving ? "Saving…" : editing ? "Save changes" : "Submit for review"}</Button>
          <Button type="button" variant="outline" onClick={() => nav2("/owner/cars")}>Cancel</Button>
        </div>
      </form>
      <KycRequiredDialog
        open={kycDialog}
        onOpenChange={(v) => { setKycDialog(v); if (!v) nav2("/owner/cars"); }}
        kycStatus={kycStatusForDialog}
        redirectTo="/owner/kyc"
        context="listing"
        onContinueAnyway={handleKycDialogContinue}
      />
    </Card>
  );
}

function OwnerBookings() {
  const { currencies } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackingId, setTrackingId] = useState(null);
  const [ratingBookingId, setRatingBookingId] = useState(null);
  const load = () => api.get("/bookings/mine", { params: { as_role: "owner" } }).then(({ data }) => setBookings(data.filter((b) => b.status !== "pending_payment"))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const confirmReturn = async (bookingId) => {
    if (!window.confirm("Confirm the car has been returned?")) return;
    try {
      await api.post(`/bookings/${bookingId}/confirm-return`);
      toast.success("Car return confirmed. Booking completed.");
      load();
    } catch(e) {
      toast.error(formatApiError(e));
    }
  };

  const act = async (id, kind) => {
    try {
      await api.post(`/bookings/${id}/${kind}`);
      toast.success(`Booking ${kind}d`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  const trackingBooking = bookings.find((b) => b.id === trackingId);
  const ratingBooking = bookings.find((b) => b.id === ratingBookingId);
  return (
    <Card className="border-border p-6">
      <h3 className="font-display text-xl font-semibold tracking-tight mb-4">Bookings for your cars</h3>
      {bookings.length === 0 ? <div className="text-sm text-muted-foreground">No bookings yet.</div> :
        <div className="space-y-3" data-testid="owner-bookings-list">
          {bookings.map((b) => {
            const now = Date.now();
            const inTripWindow = new Date(b.start_date).getTime() - 86400000 <= now && new Date(b.end_date).getTime() + 86400000 >= now;
            const canTrack = b.payment_status === "paid" && b.status === "confirmed" && inTripWindow;
            const canConfirmReturn = b.status === "confirmed" && new Date(b.start_date).getTime() <= now;
            const tripEnded = new Date(b.end_date).getTime() < now;
            const canRateCustomer = tripEnded && b.payment_status === "paid" && b.status !== "cancelled" && b.status !== "declined" && !b.owner_reviewed;
            return (
              <div key={b.id} className="border-b border-border last:border-b-0 pb-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`owner-booking-${b.id}`}>
                <div className="flex-1">
                  <div className="font-display font-semibold">{b.car_title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                    By {b.customer_name}
                    {b.customer_rating != null && (
                      <span className="inline-flex items-center gap-0.5 text-amber-600" title={`${b.customer_rating_count} rating${b.customer_rating_count === 1 ? "" : "s"} from other owners`}>
                        <Star size={11} className="fill-amber-500 text-amber-500" /> {b.customer_rating.toFixed(1)}
                      </span>
                    )}
                    · {b.start_date}{b.start_time ? ` ${b.start_time}` : ""} → {b.end_date}{b.end_time ? ` ${b.end_time}` : ""} · {b.days} day{b.days > 1 ? "s" : ""}
                  </div>
                  <Badge variant="outline" className="capitalize mt-1.5">{b.status.replace("_", " ")}</Badge>
                </div>
                <div className="text-right text-sm"><PriceDisplay amount={b.total} listingCurrency={b.currency} /></div>
                <div className="flex gap-2">
                  {canTrack && (
                    <Button size="sm" variant="outline" onClick={() => setTrackingId(b.id)} data-testid={`track-${b.id}`}>
                      <Navigation size={12} className="mr-1" /> Track
                    </Button>
                  )}
                  {canRateCustomer && (
                    <Button size="sm" variant="outline" onClick={() => setRatingBookingId(b.id)} data-testid={`rate-customer-${b.id}`}>
                      <Star size={12} className="mr-1" /> Rate customer
                    </Button>
                  )}
                  {canConfirmReturn && (
                    <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90"
                      onClick={() => confirmReturn(b.id)} data-testid={`confirm-return-${b.id}`}>
                      <CheckCircle2 size={12} className="mr-1" /> Confirm car returned
                    </Button>
                  )}
                  {b.status === "pending_payment" || b.status === "confirmed" ? (
                    <Button size="sm" variant="ghost" onClick={() => act(b.id, "decline")} data-testid={`decline-${b.id}`}>Decline</Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      }
      <LiveTripMap
        open={Boolean(trackingId)}
        onOpenChange={(o) => !o && setTrackingId(null)}
        bookingId={trackingId}
        title={trackingBooking?.car_title}
      />
      <OwnerRateCustomerDialog
        open={Boolean(ratingBookingId)}
        onOpenChange={(o) => !o && setRatingBookingId(null)}
        booking={ratingBooking}
        onSubmitted={load}
      />
    </Card>
  );
}

export default function OwnerDashboard() {
  const { user } = useAuth();
  const location = useLocation();

  // A plain customer (never listed a car) can only reach the "list a car"
  // form and identity verification here — everything else (bookings,
  // payouts, their own cars list) only makes sense once they've actually
  // become a host. Bounce them straight to the listing form instead of
  // showing an empty owner-only page.
  if (user?.role === "customer") {
    const allowedPaths = ["/owner/cars/new", "/owner/kyc"];
    if (!allowedPaths.includes(location.pathname)) {
      return <Navigate to="/owner/cars/new" replace />;
    }
  }

  // An owner who has switched to "Renting" mode shouldn't be able to reach
  // their Host dashboard just by typing the URL — send them back to their
  // renter account area until they switch back to Hosting.
  if (user?.role === "owner") {
    let mode = "renting";
    try { mode = localStorage.getItem(`rx_mode_${user.id}`) || "renting"; } catch {}
    if (mode === "renting") {
      return <Navigate to="/account" replace />;
    }
  }

  return (
    <DashboardLayout nav={nav} title="Owner dashboard" subtitle="Earn from your car">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="cars" element={<MyCars />} />
        <Route path="cars/new" element={<CarForm />} />
        <Route path="cars/:id/edit" element={<CarForm />} />
        <Route path="cars/:id/availability" element={<CarAvailability />} />
        <Route path="bookings" element={<OwnerBookings />} />
        <Route path="negotiations" element={<NegotiationsPage />} />
        <Route path="payouts" element={<OwnerPayouts />} />
        <Route path="kyc" element={<KycPage />} />
      </Routes>
    </DashboardLayout>
  );
}
